import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
	setDataLayout
} from "../../data/redux/themeSettingSlice";
import ImageWithBasePath from "../imageWithBasePath";
import {
	setMobileSidebar,
	toggleMiniSidebar,
} from "../../data/redux/sidebarSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import { all_routes } from "../../../feature-module/router/all_routes";
import { RootState } from "../../../core/data/redux/store";
import { logout } from "../../data/redux/authSlice";
import {
	setAcademicYears,
	setSelectedAcademicYear,
	setLoading,
	setError,AcademicYear
} from "../../data/redux/academicYearSlice"
import { fetchAcademicYearApi } from "../../data/services/studentService"

// Import the user actions and services
import {
	setLoading as setUserLoading,
	fetchusersSuccess,
	fetchuserDetailSuccess,
	fetchusersFailure,
	restoreFromStorage
} from "../../data/redux/userSlice";
import {
	fetchCurrentUserApi,
	fetchCurrentUserDetailApi
} from "../../data/services/userService";
import { USER_PROFILE_IMG } from "../../../constants/constants"
const Header = () => {
	const routes = all_routes
	const dispatch = useDispatch();
	const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);

	const authState = useSelector((state: RootState) => state.auth);
	const userRole = authState?.roleName;

	// Local loading state
	const [isInitializing, setIsInitializing] = useState(true);
	// Add a ref to track if we've already initialized
	const hasInitialized = useRef(false);

	// Fetch user data from the Redux store
	const user = useSelector((state: RootState) => state.user.Users);
	const userdetail = useSelector((state: RootState) => state.user.Userdetail);
	const userLoading = useSelector((state: RootState) => state.user.loading);

	const { selectedAcademicYear, academicYears, loading } = useSelector(
		(state: RootState) => state.academicYear
	);

	const mobileSidebar = useSelector(
		(state: any) => state.sidebarSlice.mobileSidebar
	);

	const toggleMobileSidebar = () => {
		dispatch(setMobileSidebar(!mobileSidebar));
	};

	const handleToggleMiniSidebar = () => {
		if (dataLayout === "mini_layout") {
			dispatch(setDataLayout("default_layout"));
			localStorage.setItem("dataLayout", "default_layout");
		} else {
			dispatch(toggleMiniSidebar());
		}
	};

	const location = useLocation();
	useEffect(() => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	}, [location]);

	// Memoize the initialization function
	const initializeUserData = useCallback(async () => {
		// Prevent multiple initializations
		if (hasInitialized.current) {
			return;
		}

		try {
			// First, try to restore from localStorage
			dispatch(restoreFromStorage());

			// Check if we have a token (user is logged in)
			const token = localStorage.getItem('token');
			if (!token) {
				setIsInitializing(false);
				hasInitialized.current = true;
				return;
			}

			// Small delay to allow restoration to complete
			await new Promise(resolve => setTimeout(resolve, 100));

			// If we don't have user data in Redux after restoration, fetch from API
			const currentUsers = user.length > 0 ? user : [];
			const currentUserDetail = userdetail;

			if (currentUsers.length === 0 || !currentUserDetail) {
				dispatch(setUserLoading(true));

				try {
					// Fetch user data from API
					const [userData, userDetailData] = await Promise.all([
						fetchCurrentUserApi(),
						fetchCurrentUserDetailApi()
					]);

					// Dispatch the data to Redux (which will also save to localStorage)
					if (userData) {
						dispatch(fetchusersSuccess(Array.isArray(userData) ? userData : [userData]));
					}
					if (userDetailData) {
						dispatch(fetchuserDetailSuccess(userDetailData));
					}
				} catch (error: any) {
					console.error("Error fetching user data:", error);
					dispatch(fetchusersFailure(error.message || "Failed to fetch user data"));
				}
			}
		} catch (error) {
			console.error("Error initializing user data:", error);
		} finally {
			setIsInitializing(false);
			hasInitialized.current = true;
		}
	}, [dispatch, user, userdetail]);

	// Initialize user data on component mount
	useEffect(() => {
		initializeUserData();
	}, [initializeUserData]);

	// Handle Logout
	const handleLogout = () => {
		// Clear user data from Redux and localStorage
		dispatch(logout());
		// Reset initialization flag
		hasInitialized.current = false;
	};

	// Get Academic Year from API
	useEffect(() => {
		const fetchAcademicYears = async () => {
			try {
				dispatch(setLoading(true));
				const response = await fetchAcademicYearApi();
				dispatch(setAcademicYears(response.data));

				const activeYear = response.data.find((year: AcademicYear) => year.isActive);
				if (activeYear) {
					dispatch(setSelectedAcademicYear(activeYear));
				} else if (response.data.length > 0) {
					dispatch(setSelectedAcademicYear(response.data[0]));
				}
			} catch (error) {
				dispatch(setError('Failed to fetch academic years'));
				console.error('Error:', error);
			} finally {
				dispatch(setLoading(false));
			}
		};

		fetchAcademicYears();
	}, [dispatch]);

	const handleAcademicYearChange = (year: AcademicYear) => {
		dispatch(setSelectedAcademicYear(year));
	};

	// Show loading state while initializing
	if (isInitializing || userLoading || loading) {
		return (
			<div className="header">
				<div className="header-left">
					<Link to={routes.teacherDashboard} className="logo logo-normal">
						<ImageWithBasePath src="assets/img/logo.svg" alt="Logo" />
					</Link>
					<button id="toggle_btn" onClick={handleToggleMiniSidebar}>
						<i className="ti ti-menu-deep" />
					</button>
				</div>
				<button
					id="mobile_btn"
					className="mobile_btn"
					onClick={toggleMobileSidebar}
				>
					<span className="bar-icon">
						<span />
						<span />
						<span />
					</span>
				</button>
				<div className="header-user">
					<div className="nav user-menu">
						<div className="school-name">Loading...</div>
						<div className="d-flex align-items-center">
							<div className="dropdown me-2">
								<button
									className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2"
									disabled
								>
									<i className="ti ti-calendar-due me-1" />
									Loading...
								</button>
							</div>
							<div className="dropdown ms-1">
								<button
									className="dropdown-toggle d-flex align-items-center"
									disabled
								>
									<span className="avatar avatar-md rounded">
										<ImageWithBasePath
											src="assets/img/teachers/teacher-01.jpg"
											alt="Img"
											className="img-fluid"
										/>
									</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Get the first user for display
	const currentUser = user.length > 0 ? user[0] : null;
	const schoolName = currentUser?.relatedEntity?.schools?.name || '';
	// const userName = userdetail ? `${ userdetail.firstName || '' } ${ userdetail.lastName || '' }`.trim() : 'User';
	// const userEmail = userdetail?.email || 'email@example.com';

	// Rest of your component JSX remains the same...
	return (
		<>
			{/* Header */}
			<div className="header">
				{/* Logo */}
				<div className="header-left">
					<Link to={routes.teacherDashboard} className="logo logo-normal">
						<ImageWithBasePath src="assets/img/logo.svg" alt="Logo" />
					</Link>
					<button id="toggle_btn" onClick={handleToggleMiniSidebar}>
						<i className="ti ti-menu-deep" />
					</button>
				</div>
				{/* /Logo */}

				<button
					id="mobile_btn"
					className="mobile_btn"
					onClick={toggleMobileSidebar}
				>
					<i className="ti ti-menu-deep" />
				</button>

				<div className="header-user">
					<div className="nav user-menu">
						{userRole !== 'admin' && (
							<div className="school-name">
								{schoolName}
							</div>
					    )}
						<div className="d-flex align-items-center">
							<div className="dropdown me-2">
								<button
									className="btn btn-outline-light fw-normal bg-white d-flex align-items-center p-2"
									data-bs-toggle="dropdown"
									aria-expanded="false"
								>
									<i className="ti ti-calendar-due me-1" />
									{selectedAcademicYear
										? `Academic Year: ${selectedAcademicYear.academicYear.replace('-', '/')}`
										: 'No academic year selected'}
								</button>
								<div className="dropdown-menu dropdown-menu-right">
									{academicYears.map((year: any) => (
										<button
											key={year.id}
											className={`dropdown-item d-flex align-items-center ${ selectedAcademicYear?.id === year.id ? 'active' : ''
												}`}
											onClick={() => handleAcademicYearChange(year)}
										>
											Academic Year: {year.academicYear.replace('-', '/')}
										</button>
									))}
								</div>
							</div>

							<div className="dropdown ms-1 ">
								<button
									className="dropdown-toggle d-flex align-items-center "
									data-bs-toggle="dropdown"
								>
									<span className="avatar avatar-md avatar-rounded">
										<ImageWithBasePath

											src={userdetail.profileImage || USER_PROFILE_IMG}
											alt="Img"
											className="img-fluid "
										/>
									</span>
								</button>
								<div className="dropdown-menu">
									<div className="d-block ">
										<Link to={routes.userViewProfile} className="d-flex align-items-center justify-content-between p-2 ">
											<span className="avatar avatar-md me-2 online avatar-rounded">
												<ImageWithBasePath
													src={userdetail.profileImage || USER_PROFILE_IMG}
													alt="img"
												/>
											</span>
											{/* <div>
												<p className="mb-0">{userName}</p>
												<small className="text-muted">{userEmail}</small>
											</div> */}
											<div className="d-flex align-items-center justify-content-end ">
												<p className="me-2" >View Profile</p>
											</div>
										</Link>

										<div className="d-flex align-items-center justify-content-end ">
											<button
												className="btn border-0"
												onClick={handleLogout}
											>
												Logout
											</button>
										</div>
										<hr className="m-0" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Mobile Menu */}
				<div className="dropdown mobile-user-menu">
					<button
						className="dropdown-toggle d-flex align-items-center"
						data-bs-toggle="dropdown"
					>
						<span className="avatar avatar-md avatar-rounded">
							<ImageWithBasePath
								src={userdetail.profileImage || USER_PROFILE_IMG}
								alt="Img"
								className="img-fluid"
							/>
						</span>
					</button>
					<div className="dropdown-menu dropdown-menu-end " data-popper-placement="bottom-end">
						<div className="d-block ">
							<Link to={routes.userViewProfile} className="d-flex align-items-center justify-content-between p-2 ">
								<span className="avatar avatar-md me-2 online avatar-rounded">
									<ImageWithBasePath
										src={userdetail.profileImage || USER_PROFILE_IMG}
										alt="img"
									/>
								</span>
								{/* <div>
									<p className="mb-0">{userName}</p>
									<small className="text-muted">{userEmail}</small>
								</div> */}
								<div className="d-flex align-items-center justify-content-end ">
									<p className="me-3" >View Profile</p>
								</div>
							</Link>

							<div className="d-flex align-items-center justify-content-end ">
								<button
									className="btn border-0"
									onClick={handleLogout}
								>
									Logout
								</button>
							</div>
							<hr className="m-0" />
						</div>
					</div>
				</div>
				{/* /Mobile Menu */}
			</div>
			{/* /Header */}
		</>
	);
};

export default Header;